import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { pt } from './pt'
import { es } from './es'
import { en } from './en'

/**
 * Sistema de tradução próprio, sem biblioteca: o app inteiro já evita
 * dependência (ver web/package.json), e três idiomas fixos não justificam o
 * peso de um react-i18next.
 *
 * `Translation<T>` garante em tempo de COMPILAÇÃO que es.ts e en.ts têm
 * exatamente as mesmas chaves de pt.ts — esquecer uma vira erro de build,
 * não buraco silencioso em produção.
 */
type Translation<T> = { [K in keyof T]: T[K] extends string ? string : Translation<T[K]> }

export type Lang = 'pt' | 'es' | 'en'

const DICIONARIOS: Record<Lang, Translation<typeof pt>> = { pt, es, en }

const CHAVE_IDIOMA = 'radioamador:idioma'

function ehLang(v: string | null): v is Lang {
  return v === 'pt' || v === 'es' || v === 'en'
}

/** localStorage, se já escolhido; senão navigator.language, com pt de reserva. */
export function detectarIdioma(): Lang {
  try {
    const salvo = localStorage.getItem(CHAVE_IDIOMA)
    if (ehLang(salvo)) return salvo
  } catch {
    // segue para a detecção do navegador
  }
  const nav = navigator.language.slice(0, 2).toLowerCase()
  if (nav === 'es') return 'es'
  if (nav === 'en') return 'en'
  return 'pt'
}

/** Substitui placeholders `{nome}` por valor — única forma de string dinâmica
 * no dicionário, pra manter todo leaf um `string` puro (Translation<T> exige). */
export function formatar(s: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s)
}

interface I18nContext {
  lang: Lang
  t: Translation<typeof pt>
  setLang: (l: Lang) => void
}

const Ctx = createContext<I18nContext | null>(null)

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectarIdioma)

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try {
      localStorage.setItem(CHAVE_IDIOMA, l)
    } catch {
      // preferência não persiste, mas continua valendo nesta sessão
    }
  }, [])

  const value = useMemo(() => ({ lang, t: DICIONARIOS[lang], setLang }), [lang, setLang])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useI18n(): I18nContext {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useI18n precisa estar dentro de <LangProvider>')
  return ctx
}
